/**
 * Login Page
 *
 * Authentication entry point with Email and SSO options.
 * Designed to work with AuthLayout's split-screen design.
 */

import { useAuth0 } from '@auth0/auth0-react';
import { Navigate, useLocation } from 'react-router-dom';
import { Mail, Building2, ArrowRight } from 'lucide-react';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { isSafeInAppPath, rememberReturnTo } from '../../lib/return-to';

export function LoginPage() {
  const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();
  const location = useLocation();

  // G-12: the deep link ProtectedRoute was asked for, if any. Both sign-in
  // buttons store it for CallbackPage. That goes through sessionStorage, never
  // Auth0 appState (see lib/return-to.ts).
  const returnTo = (location.state as { returnTo?: unknown } | null)?.returnTo;
  const safeReturnTo = isSafeInAppPath(returnTo) ? returnTo : null;

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to={safeReturnTo ?? '/dashboard'} replace />;
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <LoadingSpinner size="lg" />
        <p className="mt-4 text-sm text-gray-500">Checking authentication...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">
          Welcome back
        </h1>
        <p className="mt-2 text-sm text-gray-500 leading-relaxed">
          Sign in to access your organization's performance insights and analytics.
        </p>
      </div>

      {/* Login Options */}
      <div className="space-y-4">
        {/* Primary: Email Login */}
        <button
          onClick={() => {
            rememberReturnTo(safeReturnTo);
            return loginWithRedirect({
              authorizationParams: {
                screen_hint: 'login',
              },
            });
          }}
          className="
            group w-full flex items-center justify-between
            rounded-xl bg-guild-green px-5 py-4
            text-sm font-semibold text-white
            shadow-lg shadow-guild-green/20
            hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
            active:scale-[0.98]
            focus:outline-none focus:ring-2 focus:ring-guild-green focus:ring-offset-2
            transition-all duration-200
          "
        >
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/15">
              <Mail size={18} strokeWidth={2.5} />
            </div>
            <span>Continue with Email</span>
          </div>
          <ArrowRight
            size={18}
            className="opacity-50 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200"
          />
        </button>

        {/* Divider */}
        <div className="relative py-2">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-4 text-xs font-medium text-gray-400 uppercase tracking-wider">
              or
            </span>
          </div>
        </div>

        {/* Secondary: SSO Login */}
        <button
          onClick={() => {
            rememberReturnTo(safeReturnTo);
            return loginWithRedirect();
          }}
          className="
            group w-full flex items-center justify-between
            rounded-xl border-2 border-gray-200 bg-white px-5 py-4
            text-sm font-semibold text-gray-700
            hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900
            active:scale-[0.98]
            focus:outline-none focus:ring-2 focus:ring-guild-green focus:ring-offset-2
            transition-all duration-200
          "
        >
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100 group-hover:bg-gray-200 transition-colors">
              <Building2 size={18} strokeWidth={2} className="text-gray-600" />
            </div>
            <div className="flex flex-col items-start">
              <span>Enterprise SSO</span>
              <span className="text-xs font-normal text-gray-400">
                Azure AD, Okta, Google Workspace
              </span>
            </div>
          </div>
          <ArrowRight
            size={18}
            className="text-gray-400 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200"
          />
        </button>
      </div>

      {/* Help text */}
      <div className="pt-4 border-t border-gray-100">
        <p className="text-xs text-gray-400 leading-relaxed">
          By continuing, you agree to Guild Insights' Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  );
}
